import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'

export default function StaffLoginForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Pre-fill email from invite link
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const emailParam = params.get('email')
    if (emailParam) setFormData(prev => ({ ...prev, email: emailParam }))
  }, [location.search])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: formData.email,
      password: formData.password,
    })

    if (authError) {
      setError('Invalid email or password')
      setLoading(false)
      return
    }

    // Check if supporter record exists
    const { data: supporter } = await supabase
      .from('supporters')
      .select('role')
      .eq('id', data.user.id)
      .single()

    // No record — check for pending invite
    if (!supporter) {
      const { data: invite } = await supabase
        .from('invites')
        .select('*')
        .eq('email', formData.email)
        .eq('accepted', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (!invite) {
        await supabase.auth.signOut()
        setError('No account found. Please contact your administrator.')
        setLoading(false)
        return
      }

      // Create supporter record from invite
      const { error: createError } = await supabase.from('supporters').insert({
        id: data.user.id,
        full_name: data.user.email.split('@')[0],
        email: data.user.email,
        role: invite.role,
        club_id: invite.club_id,
        chapter_id: invite.chapter_id || null,
        status: 'active',
        membership_id: `STAFF-${Date.now()}`,
      })

      if (createError) {
        await supabase.auth.signOut()
        setError(`Account setup failed: ${createError.message}`)
        setLoading(false)
        return
      }

      // Mark invite as accepted
      await supabase.from('invites').update({ accepted: true }).eq('id', invite.id)

      // Redirect to change password on first login
      navigate('/change-password')
      return
    }

    // Existing record — check role
    const role = supporter?.role

    if (role === 'supporter') {
      await supabase.auth.signOut()
      setError('This portal is for staff only. Please use the supporter login.')
      setLoading(false)
      return
    }

    if (role === 'club_admin') navigate('/admin')
    else if (role === 'chapter_coordinator') navigate('/coordinator')
    else if (role === 'super_admin') navigate('/superadmin')
    else {
      await supabase.auth.signOut()
      setError('Unauthorized access.')
      setLoading(false)
    }
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-2">
        <label className={labelClass}>Email Address</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" required className={inputClass} />
      </div>
      <div className="flex flex-col gap-2">
        <label className={labelClass}>Password</label>
        <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required className={inputClass} />
      </div>
      <button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white py-4 font-bold uppercase tracking-widest text-sm transition-colors mt-2">
        {loading ? 'Signing In...' : 'Sign In'}
      </button>
    </form>
  )
}